import { AlertTriangle, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useI18n } from '../../i18n';

export interface AccessDeniedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoBack: () => void;
  supportEmail?: string;
}

export function AccessDeniedModal({
  isOpen,
  onClose,
  onGoBack,
  supportEmail = 'support@atlas.local',
}: AccessDeniedModalProps): JSX.Element | null {
  const { t } = useI18n();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('accessDenied.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {t('accessDenied.message')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            {t('accessDenied.help')}
          </p>

          <div className="space-y-3">
            <button
              onClick={onGoBack}
              className={cn(
                'w-full py-3 px-4 rounded-lg font-medium transition-colors',
                'bg-blue-600 text-white hover:bg-blue-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
              )}
            >
              {t('common.goBack')}
            </button>

            <a
              href={`mailto:${supportEmail}?subject=Access%20Denied%20-%20Vanguard%20Atlas`}
              className={cn(
                'block w-full py-2 text-sm text-blue-600 dark:text-blue-400',
                'hover:underline'
              )}
            >
              {t('common.contactSupport')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessDeniedModal;
